import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Switch,
} from '@chakra-ui/react'
import _ from 'lodash'
import { useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { Webhook } from '../../interfaces/WebhooksGetResponse'
import { UserContext } from '../../providers'
import { ActiveEvents, EndpointField } from './WebhookModalCommon'

interface Props {
  isOpen: boolean
  existingWebhookEndpoints: string[]
  webhook: Webhook
  onClose(): void
  reloadWebhooks(): void
  onWebhookUpdate(
    webhookId: string,
    set: Partial<Pick<Webhook, 'endpoint' | 'enabled' | 'events'>>,
  ): void
}

const WebhookEditModal = ({
  isOpen,
  existingWebhookEndpoints,
  onClose,
  reloadWebhooks,
  webhook,
  onWebhookUpdate,
}: Props): React.ReactElement => {
  if (!webhook) {
    return <br />
  }
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()
  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')

  const [endpoint, setEndpoint] = useState(webhook.endpoint)
  const [activeEvents, setActiveEvents] = useState(webhook.events)
  const [enabled, setEnabled] = useState(webhook.enabled)

  useEffect(() => {
    if (isOpen) {
      setEndpoint(webhook.endpoint)
      setActiveEvents(webhook.events)
      setEnabled(webhook.enabled)
    }
  }, [isOpen, webhook])

  const closeModal = (): void => {
    setFormError('')
    onClose()
  }

  const handleSubmit = async () => {
    const clientNumber = user?.clientNumber
    if (!clientNumber) {
      return
    }

    const updates: Partial<Pick<Webhook, 'endpoint' | 'enabled' | 'events'>> =
      {}

    // Add modified fields to updates object
    if (endpoint !== webhook.endpoint) {
      updates.endpoint = endpoint
    }
    if (enabled !== webhook.enabled) {
      updates.enabled = enabled
    }

    if (!_.isEqual(activeEvents.sort(), webhook.events.sort())) {
      updates.events = activeEvents
    }
    if (!webhook || Object.keys(updates).length === 0) {
      closeModal()
      return
    }

    await request
      .patch(
        `/api/clients/${clientNumber}/settings/developer/webhook/${webhook.id}`,
        updates,
      )
      .then(() => {
        if (request.error) {
          switch (request.error.message) {
            case 'Not Found':
              reloadWebhooks() // This situation can happen if the webhook was deleted by another user.
              throw new Error(t('webhook_not_found', 'Webhook not found'))
            case 'Conflict':
              reloadWebhooks() // This situation can happen if the webhook endpoint was added by another user.
              throw new Error(
                t(
                  'webhook_endpoint_already_exists',
                  'Webhook endpoint already exists',
                ),
              )
            default:
              throw new Error(request.error.message)
          }
        }
        onWebhookUpdate(webhook.id, updates)
        toast({
          description: t('webhook_updated', 'Webhook updated'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        closeModal()
      })
      .catch(error => {
        setFormError(error.message)
      })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('edit_webhook', 'Edit webhook')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody spacing={10}>
          {formError ? (
            <Alert status="error" mb="1em" minWidth="unset">
              <AlertIcon />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          ) : null}
          <EndpointField
            existingWebhookEndpoints={existingWebhookEndpoints.filter(
              endpoint => webhook.endpoint !== endpoint,
            )}
            endpoint={endpoint}
            setEndpoint={setEndpoint}
          />
          <FormControl display="flex" alignItems="center" mb={4}>
            <FormLabel>{t('enable_webhook', 'Enable webhook')}</FormLabel>
            <Switch
              isChecked={enabled}
              onChange={e => setEnabled(e.target.checked)}
            />
          </FormControl>
          <ActiveEvents
            activeEvents={activeEvents}
            setActiveEvents={setActiveEvents}
          />
        </ModalBody>
        <ModalFooter
          borderTop={`1px solid ${colors.appGrey}`}
          style={{ gap: '1em' }}
        >
          <Button
            onClick={() => {
              closeModal()
              setFormError('')
            }}
            variant="outline"
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            isDisabled={endpoint === '' || activeEvents.length === 0}
          >
            {t('save', 'Save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default WebhookEditModal
